// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { ChoiceGroup, IChoiceGroupOption } from 'office-ui-fabric-react/lib/ChoiceGroup';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';
import { getId } from 'office-ui-fabric-react/lib/Utilities';
import { useTranslation } from 'react-i18next';
import Styles from './address.module.scss';

import {
    Progress,
    updateCreateOrderProgress
} from './../../../store/orderCreation';
import { logPageAction } from 'src/lib/ApplicationInsights';

interface AddressChoicesType {
    hide: boolean;
    options: IChoiceGroupOption[];
    isBillingRequired: boolean;
    onCancelChoiceClicked: () => void;
    onChoiceChange?: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => void;
    onSaveChoiceClicked: () => void;
    secondSRCreation: boolean;
}

export default function AddressChoices(props: AddressChoicesType) {

    const dispatch = useDispatch();

    const [hide, hideDialog] = useState(props.hide);
    const [isSaved, save] = useState(false);

    const { t } = useTranslation();
    const choiceGroupRef = React.useRef<any>(null);

    //will watch if it has to open or close dialog from parent
    useEffect(() => {
        hideDialog(props.hide);
    }, [props.hide]);

    useEffect(() => {
        if(!hide) {
            setTimeout(() => {
                if(choiceGroupRef.current){
                    choiceGroupRef.current.focus();
            }},25);
        }    
    }, [hide]);

    function closeDialog() {
        logPageAction('Address validation back button clicked');
        props.onCancelChoiceClicked();
        hideDialog(true);
    }

    function saveAndClose() {
        logPageAction('Address validation next button clicked');
        props.onSaveChoiceClicked();
        hideDialog(true);
        save(true);
    }

    const onDismissed = () => {
        if (isSaved) {
            if (props.isBillingRequired) {
                dispatch(updateCreateOrderProgress(Progress.AddingBillingAddress));
            } else {
                dispatch(updateCreateOrderProgress(Progress.Registration));
            }
        }
    };

    const dialogOptions = props.options.map((option: { key: string; text: string; }) => ({ key: option.key, text: option.text } as IChoiceGroupOption));

    const titleId: string = getId('dialogLabel');
    const subTextId: string = getId('subTextLabel');

    return (<section>
        <Dialog
            hidden={hide}
            onDismiss={closeDialog}
            dialogContentProps={{
                titleId,
                subTextId,
                type: DialogType.normal,
                closeButtonAriaLabel: t('close'),
                title: t('address_validation'),
                subText: t('address_validation_message'),
            }}
            modalProps={{
                onDismissed,
                titleAriaId: titleId,
                subtitleAriaId: subTextId,
                isBlocking: true,
                containerClassName: `${Styles.popup}`,
            }}
        >
            <div>
                <ChoiceGroup
                    className="defaultChoiceGroup"
                    options={dialogOptions}
                    onChange={props.onChoiceChange}
                    label={t('address_validation_select')}
                    required={true}
                    componentRef={choiceGroupRef}/>
                <p>{t('address_validation_select_continue')}</p>
            </div>
            <DialogFooter>
                <button className={clsx(Styles.buttonSecondary, Styles.button, Styles.commonFocus)} onClick={closeDialog} >{t('back')}</button>
                <button className={clsx(Styles.buttonPrimary, Styles.button, Styles.commonFocus)} onClick={saveAndClose}>{t('next')}</button>
            </DialogFooter>
        </Dialog>
    </section>);
}